import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Nav, Row, Col, Modal, Button } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../Common/Breadcrumbs';
import { DeleteModal } from '../../Common/DeleteModal';
import AddNewCommunityForm from './components/AddNewCommunityForm';
import SimpleLineChart from './components/SimpleLineChart';
import { communityService } from '../../api/community/communityService';
import { getImageUrl } from '../../utils/imageUrl';
import { VideoCategory, VideoStatus } from '../../api/community/types';
import { toast } from 'react-toastify';
import './CommunityVideoDetailPage.css';
import '../../pages/ManageTournaments/ManageTournaments.css';

type TabType = 'details' | 'analytics' | 'chapters';

interface VideoDetails {
  _id: string;
  contentType: string;
  videoType: string;
  title: string;
  description: string;
  contentUrl: string;
  thumbnailUrl?: string;
  category: string;
  mediaType?: string; // 'video' or 'image' for social posts
  metadata?: {
    userId?: string;
    userName?: string;
    userProfilePic?: string;
    playerId?: string;
    playerName?: string;
    profilePic?: string;
    basicInfo?: any;
  };
  tags: string[];
  engagement?: {
    views: number;
    likes: number;
    comments: number;
    shares: number;
    saves: number;
  };
  userInteraction?: {
    liked: boolean;
    saved: boolean;
    watched: boolean;
    notInterested: boolean;
  };
  publishedAt?: string;
  scheduledPublishAt?: string;
  createdAt?: string;
  duration?: number;
  visibility?: string;
  status?: VideoStatus | string;
  seriesId?: string; // For Level Up videos
  chapterNumber?: number;
  chapterTitle?: string;
}

interface SeriesDetails {
  _id: string;
  title: string;
  description: string;
  category: string;
  coachId: string;
  coachName: string;
  coachProfilePicture?: string;
  coachSports?: string[];
  thumbnailUrl: string;
  coverImage: string;
  subCategory?: string;
  sport: string;
  tags: string[];
  difficultyLevel: string;
  createdAt: string;
  updatedAt: string;
  chapters?: Chapter[];
}

interface Chapter {
  _id: string;
  title: string;
  description?: string;
  chapterNumber: number;
  chapterTitle?: string;
  contentUrl: string;
  thumbnailUrl?: string;
  duration?: number;
  seriesId: string;
  createdAt?: string;
  publishedAt?: string;
  engagement?: {
    views: number;
    likes: number;
    comments: number;
    shares: number;
  };
}

interface AnalyticsData {
  mediaId?: string;
  videoTitle?: string;
  videoType?: string;
  publishedAt?: string;
  engagement?: {
    totalInteractions: number;
    views: number;
    likes: number;
    saves: number;
    shares: number;
    comments: number;
    notInterested: number;
    engagementRate: number;
  };
  viewerBehavior?: {
    avgWatchDuration: number;
    avgCompletionRate: number;
    videoDuration: number;
  };
  breakdown?: {
    notInterestedReasons: Record<string, number>;
    sharePlatforms: Record<string, number>;
  };
  trends?: {
    views: {
      last7Days: number;
      last30Days: number;
    };
    likes: {
      last7Days: number;
      last30Days: number;
    };
  };
  chartData?: {
    data: Array<{
      timestamp?: string; // Optional - only present for hourly interval
      date?: string; // Optional - present for hourly/daily intervals
      hour?: number; // Optional - only present for hourly interval
      weekStart?: string; // Optional - only present for weekly interval
      weekEnd?: string; // Optional - only present for weekly interval
      month?: string; // Optional - only present for monthly interval (e.g., "2025-10")
      monthStart?: string; // Optional - only present for monthly interval
      monthEnd?: string; // Optional - only present for monthly interval
      views: number;
      likes: number;
      saves: number;
      shares: number;
    }>;
    metadata: {
      days: number;
      interval: string;
      dataPoints: number;
    };
  };
}

const CommunityVideoDetailPage: React.FC = () => {
  const { category: categoryParam, videoId } = useParams<{ category: string; videoId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>('details');
  
  useEffect(() => {
    // When videoId changes, we're on a new page, so reset all state
    // BUT: if we're in the process of navigating away, preserve the navigation flag
    // This prevents analytics from being fetched during navigation transitions
    const wasNavigatingAway = isNavigatingAwayRef.current;
    
    setActiveTab('details');
    fetchedVideoIdRef.current = null;
    setSeries(null);
    setChapters([]);
    setDaysFilter(1);
    setIntervalFilter('hourly');
    setIsIntervalManuallySet(false);
    setAnalytics(null);
    // Cancel any pending analytics requests when videoId changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    fetchingAnalyticsRef.current = false;
    isMountedRef.current = true;
    
    // Only reset navigation flag if we were NOT navigating away
    // If we were navigating away, keep the flag set to prevent analytics fetch during transition
    // Use a small delay to reset the flag after navigation completes
    if (!wasNavigatingAway) {
      isNavigatingAwayRef.current = false;
    } else {
      // We were navigating away, but videoId changed - might be arriving at new page
      // Wait a bit to ensure navigation is complete before resetting
      setTimeout(() => {
        // Only reset if we're still mounted (meaning we're on the new page, not unmounting)
        if (isMountedRef.current && videoId) {
          isNavigatingAwayRef.current = false;
        }
      }, 150);
    }
  }, [videoId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      isNavigatingAwayRef.current = true;
      // Cancel any pending analytics requests on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      fetchingAnalyticsRef.current = false;
    };
  }, []);
  const [video, setVideo] = useState<VideoDetails | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [series, setSeries] = useState<SeriesDetails | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [daysFilter, setDaysFilter] = useState<number>(1);
  const [intervalFilter, setIntervalFilter] = useState<string>('hourly');
  const [isIntervalManuallySet, setIsIntervalManuallySet] = useState<boolean>(false);
  const [selectedChartMetric] = useState<'views' | 'watchTime'>('views'); // Always 'views' now

  // Dropdown options for days filter
  const daysOptions = [
    { value: 1, label: 'Last 24 hours' },
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
    { value: 365, label: 'All time' }
  ];

  // Dropdown options for interval filter
  const intervalOptions = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingChapters, setIsAddingChapters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const getCategoryFromParamHelper = (param: string | undefined): string => {
    if (param === 'action') return 'action';
    if (param === 'level_up') return 'level_up';
    if (param === 'social') return 'social';
    return 'action'; // Default
  };

  const category = categoryParam || (video?.category ? getCategoryFromParamHelper(video.category) : 'action');

  // Helper function to determine interval based on days
  const getAutoInterval = useCallback((days: number): string => {
    if (days === 1) {
      return 'hourly';
    } else if (days <= 30) {
      return 'daily';
    } else if (days <= 90) {
      return 'weekly';
    } else {
      return 'monthly';
    }
  }, []);

  const fetchingAnalyticsRef = useRef(false);
  const isManuallyRefreshingRef = useRef(false);
  const fetchedVideoIdRef = useRef<string | null>(null);
  const fetchVideoDetailsRef = useRef<() => Promise<void>>();
  const fetchSeriesDetailsForLevelUpRef = useRef<(skipVideoApiFallback?: boolean) => Promise<void>>();
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isNavigatingAwayRef = useRef(false);

  // Define all fetch functions BEFORE useEffect hooks that use them
  const fetchVideoDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await communityService.getVideoById(videoId!);
      
      let videoData = null;
      if (response && response.video) {
        videoData = response.video;
      } else if (response && response.data && response.data.video) {
        videoData = response.data.video;
      } else if (response && response.data) {
        videoData = response.data;
      } else if (response) {
        videoData = response;
      }
      
      if (videoData) {
        console.log('Video data fetched:', videoData);
        console.log('Video status:', videoData.status);
        console.log('Video publishedAt:', videoData.publishedAt);
        console.log('Video videoType:', videoData.videoType);
        
        if (!videoData.videoType && videoData.seriesId) {
          videoData.videoType = 'chapter';
          console.log('Set videoType to chapter based on seriesId presence');
        }
        if (videoData.videoType === 'chapter' && videoData.category === 'level_up' && !videoData.seriesId) {
          // Check if seriesId was passed in navigation state
          const navState = (location.state as any);
          if (navState?.seriesId) {
            videoData.seriesId = navState.seriesId;
            console.log('Using seriesId from navigation state:', navState.seriesId);
          } else {
            // Fallback: Try to find it from series list (less efficient)
            // Use cache busting to ensure we get fresh series data
            try {
              const seriesListResponse = await communityService.getAllSeries(1, 100, 'published,draft', true);
              let seriesList: any[] = [];
              if (seriesListResponse && (seriesListResponse as any).videos && Array.isArray((seriesListResponse as any).videos)) {
                seriesList = (seriesListResponse as any).videos;
              } else if ((seriesListResponse as any)?.data?.series && Array.isArray((seriesListResponse as any).data.series)) {
                seriesList = (seriesListResponse as any).data.series;
              } else if (Array.isArray((seriesListResponse as any)?.data)) {
                seriesList = (seriesListResponse as any).data;
              }
              
              // Find the series that contains this chapter
              for (const seriesItem of seriesList) {
                if (seriesItem.chapters && Array.isArray(seriesItem.chapters)) {
                  const chapterFound = seriesItem.chapters.find((ch: any) => ch._id === videoId);
                  if (chapterFound) {
                    videoData.seriesId = seriesItem._id;
                    console.log('Found seriesId for chapter:', seriesItem._id);
                    break;
                  }
                }
              }
            } catch (seriesError) {
              console.warn('Could not find seriesId for chapter:', seriesError);
            }
          }
        }
        
        // Ensure status is set - if missing and no publishedAt, likely draft/rejected
        if (!videoData.status && !videoData.publishedAt) {
          console.warn('Status missing from video data, but no publishedAt - likely draft/rejected');
        }
        
        setVideo(videoData);
      }
    } catch (error: any) {
      console.error('Error fetching video details:', error);
      toast.error(error.message || 'Failed to fetch video details');
    } finally {
      setLoading(false);
    }
  }, [videoId, location.state]);

  const fetchSeriesDetailsForLevelUp = useCallback(async (skipVideoApiFallback: boolean = false) => {
    try {
      setLoading(true);
      // Try to fetch as series first using /api/v1/community/series/:id
      const seriesResponse = await communityService.getSeriesById(videoId!);
      
      let seriesData = null;
      // Handle different response structures
      if (seriesResponse && (seriesResponse as any).series) {
        seriesData = (seriesResponse as any).series;
      } else if (seriesResponse && seriesResponse.data && (seriesResponse.data as any).series) {
        seriesData = (seriesResponse.data as any).series;
      } else if (seriesResponse && seriesResponse.data && typeof seriesResponse.data === 'object') {
        seriesData = seriesResponse.data;
      } else if (seriesResponse && typeof seriesResponse === 'object' && (seriesResponse._id || seriesResponse.title)) {
        seriesData = seriesResponse;
      }
      
      // Validate that we actually got valid series data
      // A valid series should have _id and title, and should NOT have seriesId (chapters have seriesId)
      const isValidSeries = seriesData && 
                            seriesData._id && 
                            seriesData.title && 
                            !seriesData.seriesId &&
                            seriesData._id === videoId; // Ensure the ID matches
      
      if (isValidSeries) {
        // Successfully fetched series - transform series data to video format for compatibility
        const videoData: VideoDetails = {
          _id: seriesData._id,
          title: seriesData.title,
          description: seriesData.description,
          contentUrl: seriesData.coverImage || '',
          thumbnailUrl: seriesData.thumbnailUrl || seriesData.coverImage || '',
          category: 'level_up',
          videoType: 'series',
          contentType: 'long_form',
          mediaType: 'video',
          tags: seriesData.tags || [],
          metadata: {
            userName: seriesData.coachName,
            userProfilePic: seriesData.coachProfilePicture,
            basicInfo: {
              coachId: seriesData.coachId,
              ...(seriesData.coachSports ? { sports: seriesData.coachSports } : {}),
              ...(seriesData.coachBasicInfo || {})
            }
          },
          status: seriesData.status || 'published',
          publishedAt: seriesData.publishedAt,
          createdAt: seriesData.createdAt,
          duration: seriesData.totalDuration || 0
          // Note: seriesId is NOT set for series - it's only set for chapters
        };
        
        setVideo(videoData);
        setSeries(seriesData);
        
        // Load chapters
        if (seriesData.chapters && Array.isArray(seriesData.chapters)) {
          setChapters(seriesData.chapters);
        }
        // Series fetched successfully - do NOT call video API
        setLoading(false);
        return;
      } else {
        // No valid series data found - it's likely a chapter, fetch using video API: /api/v1/community/video/:id
        // Only fallback to video API if skipVideoApiFallback is false (i.e., during initial load)
        if (!skipVideoApiFallback && fetchVideoDetailsRef.current) {
          console.log('No valid series data found (or data indicates chapter), fetching as chapter using video API');
          await fetchVideoDetailsRef.current();
        } else {
          console.log('No valid series data found, but skipping video API fallback (skipVideoApiFallback=true)');
          setLoading(false);
        }
        return;
      }
    } catch (error: any) {
      console.error('Error fetching series details:', error);
      // NEVER call video API if skipVideoApiFallback is true (manual refresh after update)
      if (skipVideoApiFallback) {
        console.log('Error during manual refresh - NOT calling video API fallback');
        toast.error(error?.message || 'Failed to fetch series details');
        setLoading(false);
        return;
      }
      
      // Only fall back to video API if it's a 404 (not found) - indicating it's a chapter, not a series
      // AND if we're not skipping the fallback (i.e., during initial load, not manual refresh)
      if ((error?.response?.status === 404 || error?.status === 404) && fetchVideoDetailsRef.current) {
        console.log('Series not found (404), fetching as chapter using video API');
        await fetchVideoDetailsRef.current();
      } else {
        // For other errors, don't fall back to video API - show error
        toast.error(error?.message || 'Failed to fetch series details');
        setLoading(false);
      }
    }
  }, [videoId]);
  
  // Update refs when functions change (must be after function declarations)
  useEffect(() => {
    fetchVideoDetailsRef.current = fetchVideoDetails;
    fetchSeriesDetailsForLevelUpRef.current = fetchSeriesDetailsForLevelUp;
  }, [fetchVideoDetails, fetchSeriesDetailsForLevelUp]);

  const fetchSeriesDetails = useCallback(async (useCacheBust: boolean = true) => {
    if (!video?.seriesId) return;
    
    try {
      // Use cache busting by default to ensure fresh series data (especially important after series updates)
      const response = await communityService.getSeriesById(video.seriesId, useCacheBust);
      
      let seriesData = null;
      if (response && response.series) {
        seriesData = response.series;
      } else if (response && response.data && response.data.series) {
        seriesData = response.data.series;
      } else if (response && response.data) {
        seriesData = response.data;
      } else if (response) {
        seriesData = response;
      }
      
      if (seriesData) {
        console.log('Series data fetched for chapter:', seriesData.title);
        setSeries(seriesData);
      }
    } catch (error: any) {
      console.error('Error fetching series details:', error);
      toast.error(error.message || 'Failed to fetch series details');
    }
  }, [video?.seriesId]);

  const fetchSeriesChapters = useCallback(async () => {
    if (!video?.seriesId) return;
    
    try {
      setChaptersLoading(true);
      const chaptersData = await communityService.getSeriesChapters(video.seriesId);
      setChapters(chaptersData || []);
    } catch (error: any) {
      console.error('Error fetching chapters:', error);
      toast.error(error.message || 'Failed to fetch chapters');
      setChapters([]);
    } finally {
      setChaptersLoading(false);
    }
  }, [video?.seriesId]);

  const fetchAnalytics = useCallback(async () => {
    console.log('fetchAnalytics called with:', { videoId, activeTab, isMounted: isMountedRef.current, isNavigatingAway: isNavigatingAwayRef.current });
    
    // Don't fetch if component is unmounting, navigating away, or videoId is not available
    if (!videoId || !isMountedRef.current || isNavigatingAwayRef.current) {
      console.log('Skipping analytics fetch - no videoId, component unmounting, or navigating away', { videoId, isMounted: isMountedRef.current, isNavigatingAway: isNavigatingAwayRef.current });
      return;
    }
    
    // Only fetch if we're actually on the analytics tab
    if (activeTab !== 'analytics') {
      console.log('Skipping analytics fetch - not on analytics tab', { activeTab });
      return;
    }
    
    // Cancel any previous request (this allows new requests to cancel old ones when filters change)
    if (abortControllerRef.current) {
      console.log('Cancelling previous analytics request to start a new one');
      abortControllerRef.current.abort();
    }
    
    // Reset the fetching flag since we're starting a new request
    fetchingAnalyticsRef.current = false;
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const currentAbortController = abortControllerRef.current;
    
    fetchingAnalyticsRef.current = true;
    try {
      // Double-check navigation flag right before making API call
      if (isNavigatingAwayRef.current || !isMountedRef.current) {
        console.log('Skipping analytics API call - navigating away or unmounted');
        return;
      }
      
      // Check if abort signal is already aborted before making the call
      if (currentAbortController.signal.aborted) {
        console.log('Skipping analytics API call - abort signal already aborted');
        return;
      }
      
      setAnalyticsLoading(true);
      console.log('Fetching analytics for video:', videoId, 'with params:', { days: daysFilter, interval: intervalFilter });
      
      // Pass abort signal to the API call so it can be cancelled
      const response = await communityService.getVideoAnalytics(videoId, daysFilter, intervalFilter, currentAbortController.signal);
      
      // Check if request was aborted, component unmounted, or navigating away
      if (currentAbortController.signal.aborted || !isMountedRef.current || isNavigatingAwayRef.current) {
        console.log('Analytics request aborted, component unmounted, or navigating away');
        return;
      }
      
      console.log('Analytics API response received:', response);
      
      // The service already extracts response.data, so response should be the analytics data object
      // Response structure: { mediaId, engagement, viewerBehavior, breakdown, trends, chartData }
      if (response) {
        // Transform chartData to normalize API response structure
        // API may return weekly data with weekStart/weekEnd, or other intervals with date/timestamp
        let transformedResponse = { ...response };
        
        if (response.chartData && response.chartData.data && Array.isArray(response.chartData.data)) {
          transformedResponse.chartData = {
            ...response.chartData,
            data: response.chartData.data.map((point: any) => {
              // For weekly intervals, ensure weekStart and weekEnd are preserved
              if (response.chartData.metadata?.interval === 'weekly') {
                return {
                  weekStart: point.weekStart,
                  weekEnd: point.weekEnd,
                  views: point.views || 0,
                  likes: point.likes || 0,
                  saves: point.saves || 0,
                  shares: point.shares || 0,
                };
              } else if (response.chartData.metadata?.interval === 'monthly') {
                // For monthly intervals, preserve month, monthStart, monthEnd
                return {
                  month: point.month,
                  monthStart: point.monthStart,
                  monthEnd: point.monthEnd,
                  date: point.monthStart, // Use monthStart as date for compatibility
                  views: point.views || 0,
                  likes: point.likes || 0,
                  saves: point.saves || 0,
                  shares: point.shares || 0,
                };
              } else {
                // For hourly/daily, use date or timestamp
                return {
                  timestamp: point.timestamp,
                  date: point.date || point.timestamp,
                  hour: point.hour,
                  views: point.views || 0,
                  likes: point.likes || 0,
                  saves: point.saves || 0,
                  shares: point.shares || 0,
                };
              }
            })
          };
        }
        
        // Check again before setting state
        if (!isMountedRef.current || currentAbortController.signal.aborted || isNavigatingAwayRef.current) {
          console.log('Skipping state update - component unmounted, request aborted, or navigating away');
          return;
        }
        
        console.log('Setting analytics data:', transformedResponse);
        setAnalytics(transformedResponse);
      } else {
        if (!isMountedRef.current || currentAbortController.signal.aborted || isNavigatingAwayRef.current) {
          return;
        }
        console.warn('No analytics data in response');
        setAnalytics(null);
      }
    } catch (error: any) {
      // Don't handle errors if request was aborted, component unmounted, or navigating away
      if (currentAbortController.signal.aborted || !isMountedRef.current || isNavigatingAwayRef.current) {
        console.log('Analytics request aborted, component unmounted, or navigating away - ignoring error');
        return;
      }
      
      // Don't show error if it's an abort error
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        console.log('Analytics request was aborted');
        return;
      }
      
      console.error('Error fetching analytics:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (!isMountedRef.current || isNavigatingAwayRef.current) {
        return;
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch analytics';
      toast.error(errorMessage);
      setAnalytics(null);
    } finally {
      // Only update state if component is still mounted and not navigating away
      if (isMountedRef.current && !currentAbortController.signal.aborted && !isNavigatingAwayRef.current) {
        setAnalyticsLoading(false);
      }
      fetchingAnalyticsRef.current = false;
      abortControllerRef.current = null;
    }
  }, [videoId, daysFilter, intervalFilter, activeTab]);

  useEffect(() => {
    // Skip if we're manually refreshing (to prevent duplicate calls)
    if (isManuallyRefreshingRef.current) {
      console.log('Skipping useEffect - manual refresh in progress');
      return;
    }
    
    if (videoId) {
      // For Level Up series, check if we should use series API
      const currentCategory = categoryParam || 'action';
      if (currentCategory === 'level_up') {
        // Check if we navigated from chapters tab (indicated by location state)
        // If location state indicates this is a chapter, directly use video API
        // IMPORTANT: Check location.state FIRST, even if we've fetched before,
        // because navigation state takes precedence over cached data
        const navState = location.state as any;
        const isChapterFromState = navState?.isChapter === true;
        const fromChaptersTab = navState?.fromChaptersTab === true;
        
        if (isChapterFromState || fromChaptersTab) {
          // This is a chapter - directly use video API to avoid unnecessary series API call
          // Reset fetchedVideoIdRef to allow fetching chapter data even if series data was fetched before
          fetchedVideoIdRef.current = null;
          
          // IMPORTANT: Clear series and chapters state when navigating to a chapter
          // This prevents showing series UI (tabs, etc.) for chapter pages
          setSeries(null);
          setChapters([]);
          
          const detectionMethod = isChapterFromState ? 'from navigation state (isChapter)' : 
                                 'from navigation state (fromChaptersTab)';
          console.log('Chapter detected', detectionMethod, ', using video API directly');
          fetchedVideoIdRef.current = videoId; // Mark as fetched
          if (fetchVideoDetailsRef.current) {
            fetchVideoDetailsRef.current();
          }
          return; // Early return to prevent further execution
        }
        
        // Prevent infinite loops: if we've already fetched data for this videoId, skip
        // But only if location.state doesn't indicate it's a chapter (checked above)
        if (fetchedVideoIdRef.current === videoId) {
          console.log('Skipping useEffect - already fetched data for this videoId:', videoId);
          return;
        }
        
        // Unknown ID - try series API first, fallback to video API if 404
        fetchedVideoIdRef.current = videoId; // Mark as fetched before calling
        if (fetchSeriesDetailsForLevelUpRef.current) {
          fetchSeriesDetailsForLevelUpRef.current(false);
        }
      } else {
        // Prevent infinite loops: if we've already fetched data for this videoId, skip
        if (fetchedVideoIdRef.current === videoId) {
          console.log('Skipping useEffect - already fetched data for this videoId:', videoId);
          return;
        }
        
        fetchedVideoIdRef.current = videoId; // Mark as fetched
        if (fetchVideoDetailsRef.current) {
          fetchVideoDetailsRef.current();
        }
      }
    }
  }, [videoId, categoryParam, location.state]);

  // Auto-select interval based on days filter (only if not manually set)
  useEffect(() => {
    if (!isIntervalManuallySet) {
      const autoInterval = getAutoInterval(daysFilter);
      // Always update if the auto interval differs from current
      if (intervalFilter !== autoInterval) {
        console.log('Auto-selecting interval:', autoInterval, 'for days:', daysFilter);
        setIntervalFilter(autoInterval);
      }
    }
  }, [daysFilter, isIntervalManuallySet, getAutoInterval]);

  useEffect(() => {
    // Don't run if component is unmounting or navigating away
    // Also check navigation flag multiple times to prevent race conditions
    if (!isMountedRef.current || isNavigatingAwayRef.current) {
      return;
    }
    
    // Double-check navigation flag - it might have been set during render
    if (isNavigatingAwayRef.current) {
      return;
    }
    
    const currentCategory = categoryParam || (video?.category ? getCategoryFromParamHelper(video.category) : 'action');
    // Fetch analytics for regular videos and level_up chapters (not series)
    // Only fetch if we're explicitly on the analytics tab
    // Triple-check navigation flag before fetching
    if (activeTab === 'analytics' && videoId && isMountedRef.current && !isNavigatingAwayRef.current) {
      // Final check before calling fetchAnalytics
      if (isNavigatingAwayRef.current) {
        return;
      }
      
      // Allow analytics for level_up chapters (not series) and non-level_up videos
      // For level_up: only skip if we KNOW it's a series (videoType === 'series')
      // Otherwise, fetch analytics (chapters, or video not loaded yet)
      // For other categories: always fetch
      const isLevelUpSeries = currentCategory === 'level_up' && video?.videoType === 'series';
      
      console.log('Analytics useEffect triggered:', { 
        activeTab,
        videoId, 
        currentCategory, 
        videoType: video?.videoType, 
        seriesId: video?.seriesId, 
        hasVideo: !!video,
        isLevelUp: currentCategory === 'level_up',
        isLevelUpSeries,
        isMounted: isMountedRef.current,
        isNavigatingAway: isNavigatingAwayRef.current
      });
      
      if (isLevelUpSeries) {
        // Skip analytics for level_up series
        console.log('Skipping analytics fetch - this is a level_up series, not a chapter');
      } else {
        // Fetch analytics for:
        // 1. level_up chapters (videoType === 'chapter' or has seriesId)
        // 2. level_up videos that aren't series (video not loaded yet, or videoType is undefined/null)
        // 3. All non-level_up videos (action, social)
        console.log('Calling fetchAnalytics from useEffect - will fetch analytics');
        fetchAnalytics();
      }
    } else if (activeTab === 'chapters') {
      // Only load chapters for Level Up series, not for chapters
      if (video?.videoType === 'series' && series && series.chapters) {
        setChapters(series.chapters);
      } else if (video?.seriesId && video.videoType === 'series') {
        // Only fetch chapters if it's a series
        fetchSeriesChapters();
      }
    }
    
    // Cleanup function: only cancel analytics request if we're leaving analytics tab or component unmounting
    return () => {
      // Only abort if we're actually leaving the analytics tab or navigating away
      // Don't abort if we're just updating dependencies while still on analytics tab
      if (isNavigatingAwayRef.current || !isMountedRef.current) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        fetchingAnalyticsRef.current = false;
      }
      // Note: We don't abort here if dependencies change but we're still on analytics tab
      // The fetchAnalytics function itself will handle canceling previous requests when starting a new one
    };
  }, [activeTab, videoId, video, video?.seriesId, video?.videoType, video?.category, series, categoryParam, fetchAnalytics, fetchSeriesChapters]);
  
  // Separate effect for analytics filter changes - only when on analytics tab
  useEffect(() => {
    // Don't run if component is unmounting, navigating away, or not on analytics tab
    if (!isMountedRef.current || isNavigatingAwayRef.current || activeTab !== 'analytics') {
      return;
    }
    
    const currentCategory = categoryParam || (video?.category ? getCategoryFromParamHelper(video.category) : 'action');
    if (activeTab === 'analytics' && videoId && isMountedRef.current && !isNavigatingAwayRef.current) {
      // Allow analytics for level_up chapters (not series) and non-level_up videos
      // For level_up: only skip if we KNOW it's a series (videoType === 'series')
      // Otherwise, fetch analytics (chapters, or video not loaded yet)
      // For other categories: always fetch
      const isLevelUpSeries = currentCategory === 'level_up' && video?.videoType === 'series';
      
      if (isLevelUpSeries) {
        // Skip analytics for level_up series when filters change
        console.log('Skipping analytics fetch (filter change) - this is a level_up series, not a chapter');
      } else {
        // Fetch analytics for:
        // 1. level_up chapters (videoType === 'chapter' or has seriesId)
        // 2. level_up videos that aren't series (video not loaded yet, or videoType is undefined/null)
        // 3. All non-level_up videos (action, social)
        console.log('Fetching analytics (filter change):', { 
          videoId, 
          currentCategory, 
          videoType: video?.videoType, 
          seriesId: video?.seriesId, 
          hasVideo: !!video,
          isLevelUp: currentCategory === 'level_up',
          isSeries: isLevelUpSeries
        });
        fetchAnalytics();
      }
    }
    
    // Cleanup function: only cancel analytics request if we're leaving analytics tab or component unmounting
    return () => {
      // Only abort if we're actually leaving the analytics tab or navigating away
      // Don't abort if we're just updating filters while still on analytics tab
      if (isNavigatingAwayRef.current || !isMountedRef.current) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        fetchingAnalyticsRef.current = false;
      }
      // Note: We don't abort here if filters change but we're still on analytics tab
      // The fetchAnalytics function itself will handle canceling previous requests when starting a new one
    };
  }, [daysFilter, intervalFilter, activeTab, videoId, video, categoryParam, video?.category, video?.videoType, fetchAnalytics]);

  useEffect(() => {
    // Fetch series details when video is loaded and has seriesId (for chapters)
    // Always use cache busting to ensure fresh series data (important after series updates)
    const currentCategory = categoryParam || (video?.category ? getCategoryFromParamHelper(video.category) : 'action');
    if (video?.seriesId && currentCategory === 'level_up' && video.videoType !== 'series') {
      // Only fetch series details if it's a chapter, not a series itself
      // Use cache busting to ensure we get the latest series data
      fetchSeriesDetails(true);
    }
  }, [video?.seriesId, video?.category, video?.videoType, categoryParam, fetchSeriesDetails]);

  const getCategoryLabel = (category: string): string => {
    if (category === 'action') return 'Action';
    if (category === 'level_up') return 'Level Up';
    if (category === 'social') return 'Social';
    return category;
  };


  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(',', ' at');
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      const kValue = num / 1000;
      if (kValue >= 1 && kValue < 10) {
        return `${kValue.toFixed(1)}K`;
      }
      return `${Math.floor(kValue)}K`;
    }
    return num.toString();
  };

  const handleChapterClick = (chapter: Chapter) => {
    // Navigate to chapter detail page
    // The chapter detail page will use /api/v1/community/video/:id API
    // Pass state to indicate this is a chapter and include seriesId if available
    const currentCategory = categoryParam || (video?.category ? getCategoryFromParamHelper(video.category) : 'action');
    const seriesId = video?._id || series?._id || chapter.seriesId;
    
    // Navigate with replace: false to preserve browser history and ensure state is passed correctly
    navigate(`/community/${currentCategory}/${chapter._id}`, { 
      replace: false,
      state: { 
        isChapter: true, 
        chapterId: chapter._id,
        seriesId: seriesId, // Pass seriesId if available
        fromChaptersTab: true // Additional flag to help identify navigation from chapters tab
      } 
    });
  };

  // Determine if we should show Publish or Unpublish based on video status
  const shouldShowPublish = () => {
    if (!video) {
      return false;
    }
    
    // If status exists, use it
    if (video.status) {
      const status = String(video.status).toLowerCase().trim();
      const publishedStatus = VideoStatus.PUBLISHED.toLowerCase();
      
      console.log('Checking status:', { 
        rawStatus: video.status, 
        normalizedStatus: status, 
        publishedStatus: publishedStatus,
        shouldShowPublish: status !== publishedStatus 
      });
      
      // Show Publish button for non-published statuses (draft, rejected, scheduled, archived)
      // Show Unpublish button only for published status
      return status !== publishedStatus;
    }
    
    // If status is missing, check publishedAt as fallback
    // If no publishedAt, it's likely draft/rejected/scheduled - show Publish
    if (!video.publishedAt) {
      console.log('No status and no publishedAt - showing Publish button');
      return true;
    }
    
    // If publishedAt exists but no status, assume published - show Unpublish
    console.log('No status but publishedAt exists - showing Unpublish button');
    return false;
  };

  const handlePublish = () => {
    setShowPublishModal(true);
  };

  const confirmPublish = async () => {
    if (!video || !videoId) return;
    
    // Check if it's a Level Up series
    const isLevelUpSeries = category === 'level_up' && 
      (video?.contentType === 'series' || video?.videoType === 'series');
    const publishId = isLevelUpSeries ? video._id : videoId;
    
    // For Level Up series, validate that it has chapters before publishing
    if (isLevelUpSeries) {
      // Check chapters from series object or chapters state (detail page) or totalChapters (listing pages)
      let totalChapters = 0;
      if (series && series.chapters && Array.isArray(series.chapters)) {
        // Detail page: use series.chapters array length
        totalChapters = series.chapters.length;
      } else if (chapters && Array.isArray(chapters)) {
        // Fallback: use chapters state
        totalChapters = chapters.length;
      } else {
        // Fallback: check video.totalChapters (for listing pages)
        totalChapters = (video as any)?.totalChapters || 0;
      }
      
      if (totalChapters === 0) {
        toast.error('Cannot publish series without chapters. Please add at least one chapter before publishing.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        setShowPublishModal(false);
        return;
      }
    }
    
    try {
      setIsPublishing(true);
      
      if (isLevelUpSeries) {
        await communityService.publishSeries(publishId);
        toast.success('Series published successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } else {
        await communityService.publishVideo(publishId);
        toast.success('Video published successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
      
      setShowPublishModal(false);
      
      // Refresh video details
      if (isLevelUpSeries) {
        fetchSeriesDetailsForLevelUp();
      } else {
        fetchVideoDetails();
      }
    } catch (error: any) {
      console.error(`Error publishing ${isLevelUpSeries ? 'series' : 'video'}:`, error);
      
      let errorMessage = isLevelUpSeries 
        ? 'Failed to publish series. Please try again.'
        : 'Failed to publish video. Please try again.';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUnpublish = () => {
    setShowUnpublishModal(true);
  };

  const confirmUnpublish = async () => {
    if (!video || !videoId) return;
    
    try {
      setIsUnpublishing(true);
      await communityService.rejectVideo(videoId);
      
      toast.success('Video unpublished successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      
      setShowUnpublishModal(false);
      
      // Refresh video details
      fetchVideoDetails();
    } catch (error: any) {
      console.error('Error unpublishing video:', error);
      
      let errorMessage = 'Failed to unpublish video. Please try again.';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsUnpublishing(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteError(null);
  };

  const confirmDeleteVideo = async () => {
    if (!video || !videoId) return;

    // Check if it's a Level Up series
    const isLevelUpSeries = category === 'level_up' && video?.videoType === 'series';
    const deleteId = isLevelUpSeries ? video._id : videoId;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      if (isLevelUpSeries) {
        // For Level Up series, use archive API instead of delete
        await communityService.archiveSeries(deleteId);
        toast.success('Series archived successfully');
      } else {
        await communityService.deleteVideo(deleteId);
        toast.success('Video deleted successfully');
      }
      
      // Navigate back to category page
      const categoryPath = categoryParam || getCategoryFromParamHelper(video?.category);
      navigate(`/community/${categoryPath}`);
    } catch (error: any) {
      console.error(`Error ${isLevelUpSeries ? 'archiving' : 'deleting'} ${isLevelUpSeries ? 'series' : 'video'}:`, error);
      
      let errorMessage = isLevelUpSeries 
        ? 'Failed to archive series. Please try again.'
        : 'Failed to delete video. Please try again.';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setDeleteError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditSuccess = () => {
    setIsEditing(false);
    // Refresh details - use series API for Level Up series, video API for others
    const currentCategory = categoryParam || (video?.category ? getCategoryFromParamHelper(video.category) : 'action');
    if (currentCategory === 'level_up' && video?.videoType === 'series') {
      // Set flag SYNCHRONOUSLY before any async operations to prevent useEffect from interfering
      isManuallyRefreshingRef.current = true;
      console.log('Manual refresh started - setting flag to prevent useEffect');
      
      // Use the ref to call the function to ensure we're using the latest version
      if (fetchSeriesDetailsForLevelUpRef.current) {
        fetchSeriesDetailsForLevelUpRef.current(true).finally(() => {
          // Reset flag after a delay to allow state updates to complete
          setTimeout(() => {
            console.log('Manual refresh completed - resetting flag');
            isManuallyRefreshingRef.current = false;
          }, 500); // Increased delay to ensure useEffect doesn't run
        });
      }
    } else {
      if (fetchVideoDetailsRef.current) {
        fetchVideoDetailsRef.current();
      }
    }
  };

  const handleCloseEditForm = () => {
    setIsEditing(false);
  };

  const handleAddChapters = () => {
    setIsAddingChapters(true);
  };

  const handleCloseAddChaptersForm = () => {
    setIsAddingChapters(false);
    const currentCategory = categoryParam || (video?.category ? getCategoryFromParamHelper(video.category) : 'action');
    if (currentCategory === 'level_up' && video?.videoType === 'series') {
      isManuallyRefreshingRef.current = true;
      if (fetchSeriesDetailsForLevelUpRef.current) {
        fetchSeriesDetailsForLevelUpRef.current(true).finally(() => {
          setTimeout(() => {
            isManuallyRefreshingRef.current = false;
          }, 500);
        });
      }
    }
  };

  const handleChapterAdded = async () => {
    const currentCategory = categoryParam || (video?.category ? getCategoryFromParamHelper(video.category) : 'action');
    if (currentCategory === 'level_up' && video?.videoType === 'series') {
      isManuallyRefreshingRef.current = true;
      try {
        if (fetchSeriesDetailsForLevelUpRef.current) {
          await fetchSeriesDetailsForLevelUpRef.current(true);
        }
      } catch (error) {
        console.error('Error refreshing chapters after upload:', error);
      } finally {
        setTimeout(() => {
          isManuallyRefreshingRef.current = false;
        }, 500);
      }
    }
  };

  const handleAddChaptersSuccess = () => {
    setIsAddingChapters(false);
    // Refresh chapters data
    const currentCategory = categoryParam || (video?.category ? getCategoryFromParamHelper(video.category) : 'action');
    if (currentCategory === 'level_up' && video?.videoType === 'series') {
      // Refresh series data to get updated chapters
      isManuallyRefreshingRef.current = true;
      if (fetchSeriesDetailsForLevelUpRef.current) {
        fetchSeriesDetailsForLevelUpRef.current(true).finally(() => {
          setTimeout(() => {
            isManuallyRefreshingRef.current = false;
          }, 500);
        });
      }
    }
  };

  const handleSyncSeries = async () => {
    if (!video || !video._id) {
      toast.error('Series ID not found');
      return;
    }

    try {
      setIsSyncing(true);
      await communityService.syncSeriesProgress(video._id);
      
      toast.success('Series sync initiated successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (error: any) {
      console.error('Error syncing series:', error);
      
      let errorMessage = 'Failed to sync series. Please try again.';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const getBreadcrumbItems = () => {
    if (!video && !categoryParam) return [];
    
    const category = categoryParam || getCategoryFromParamHelper(video?.category);
    const categoryLabel = getCategoryLabel(category);
    
    return [
      { label: 'Video', onClick: () => navigate('/community') },
      { label: categoryLabel, onClick: () => navigate(`/community/${category}`) },
      { label: `${categoryLabel} Details`, active: true }
    ];
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-state">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="empty-state">
            <p>Video not found</p>
          </div>
        </Container>
      </div>
    );
  }

  // Category is already defined above, just get label
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const categoryLabel = getCategoryLabel(category);
  
  // Determine if it's a video post for Social category
  // Check mediaType first, then fallback to contentUrl extension
  const isVideoPost = category === 'social' && (
    video?.mediaType === 'video' || 
    (video?.contentUrl && /\.(m3u8|mp4|mov|webm|ogg)$/i.test(video.contentUrl))
  );
  
  // For video posts, use thumbnailUrl for thumbnail, contentUrl for video
  // For image posts, use contentUrl for image
  const thumbnailUrl = video?.thumbnailUrl || video?.contentUrl;
  const imageUrl = getImageUrl(thumbnailUrl);
  const videoUrl = video?.contentUrl ? getImageUrl(video.contentUrl) : null;
  const thumbnailImageUrl = video?.thumbnailUrl ? getImageUrl(video.thumbnailUrl) : imageUrl;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const engagement = video?.engagement || { views: 0, likes: 0, comments: 0, shares: 0, saves: 0 };

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={getBreadcrumbItems()} />
        </div>

        {/* Page Header */}
        <div style={{ padding: '0 15px' }}>
          <div className="d-flex align-items-center mb-4" style={{ gap: '16px' }}>
            <button 
              className="back-button"
              onClick={() => {
                // IMMEDIATELY set navigation flag to prevent any new API calls
                isNavigatingAwayRef.current = true;
                
                // Cancel any pending analytics requests before navigating
                if (abortControllerRef.current) {
                  abortControllerRef.current.abort();
                  abortControllerRef.current = null;
                }
                fetchingAnalyticsRef.current = false;
                isMountedRef.current = false;
                
                // Use browser history to go back to previous page
                // This will naturally take user back to /community if they came from there
                // or to /community/{category} if they came from category page
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  // Fallback: if no history, go to main community page
                  navigate('/community');
                }
              }}
              style={{ position: 'relative', zIndex: 10 }}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <h1 className="page-title mb-0">{video?.title || 'Loading...'}</h1>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ padding: '0 15px' }}>
          <Nav variant="tabs" className="tournament-tabs mb-4" style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
            <Nav.Item>
              <Nav.Link 
                className="tournament-tab"
                active={activeTab === 'details'}
                onClick={() => setActiveTab('details')}
              >
                {category === 'level_up' && video?.videoType === 'series' ? 'Series Details' : 'Details'}
              </Nav.Link>
            </Nav.Item>
            {category === 'level_up' && video?.videoType === 'series' ? (
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'chapters'}
                  onClick={() => setActiveTab('chapters')}
                >
                  Chapters
                </Nav.Link>
              </Nav.Item>
            ) : category === 'level_up' && video?.videoType !== 'series' ? (
              // Analytics tab for Level Up chapters (not series)
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'analytics'}
                  onClick={() => setActiveTab('analytics')}
                >
                  Analytics
                </Nav.Link>
              </Nav.Item>
            ) : category !== 'level_up' ? (
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'analytics'}
                  onClick={() => setActiveTab('analytics')}
                >
                  Analytics
                </Nav.Link>
              </Nav.Item>
            ) : null}
          </Nav>

          {/* Tab Content */}
          {activeTab === 'details' && (
            <div className="video-details-tab">
              {isEditing && video ? (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="section-label mb-0">Edit Video</h5>
                    <button className="btn btn-secondary" onClick={handleCloseEditForm}>
                      Cancel
                    </button>
                  </div>
                  <AddNewCommunityForm
                    show={true}
                    onHide={handleCloseEditForm}
                    category={category === 'action' ? VideoCategory.ACTION : category === 'level_up' ? VideoCategory.LEVEL_UP : VideoCategory.SOCIAL}
                    onSuccess={handleEditSuccess}
                    video={video}
                    mode="edit"
                    inline={true}
                  />
                </div>
              ) : (
                <>
                  {/* Details Heading and Action Buttons Row */}
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-label mb-0">Details</h5>
                    <div className="d-flex gap-2">
                      {category === 'level_up' && video?.videoType === 'series' && (
                        <button 
                          className="btn btn-info" 
                          onClick={handleSyncSeries}
                          disabled={isSyncing}
                        >
                          <i className="ph-arrows-clockwise me-2"></i>
                          {isSyncing ? 'Syncing...' : 'Sync Series'}
                        </button>
                      )}
                      {/* Show Publish button for Level Up series when not published */}
                      {(() => {
                        const isLevelUpSeries = category === 'level_up' && video?.videoType === 'series';
                        const isStandaloneLevelUp = category === 'level_up' && (
                          video?.videoType === 'single' || 
                          (video?.contentType === 'long_form' && video?.videoType !== 'series' && !video?.seriesId)
                        );
                        
                        // For Level Up series: show Publish button if not published, never show Unpublish
                        if (isLevelUpSeries) {
                          return shouldShowPublish() ? (
                            <button className="btn btn-primary" onClick={handlePublish}>
                              <i className="ph-eye me-2"></i>
                              Publish
                            </button>
                          ) : null;
                        }
                        
                        // For standalone Level Up: hide both buttons (existing behavior)
                        if (isStandaloneLevelUp) {
                          return null;
                        }
                        
                        // For other videos: show Publish/Unpublish based on status
                        return (
                          <>
                            {shouldShowPublish() ? (
                              <button className="btn btn-primary" onClick={handlePublish}>
                                <i className="ph-eye me-2"></i>
                                Publish
                              </button>
                            ) : (
                              <button className="btn btn-outline-primary" onClick={handleUnpublish}>
                                <i className="ph-eye-slash me-2"></i>
                                Unpublish
                              </button>
                            )}
                          </>
                        );
                      })()}
                      <button className="btn btn-dark" onClick={handleEdit}>
                        <i className="ph-pencil me-2"></i>
                        Edit
                      </button>
                      <button className="btn btn-danger" onClick={handleDelete}>
                        <i className="ph-trash me-2"></i>
                        Delete
                      </button>
                    </div>
                  </div>

              {/* Video/Image Section - Hide for Level Up series */}
              {!(category === 'level_up' && video?.videoType === 'series') && (
                <div className="video-section mb-4">
                  {category === 'social' ? (
                  // For Social category, check if it's a video post
                  isVideoPost ? (
                    // Show video + thumbnail for video posts
                    <div className="video-thumbnail-row">
                      {/* Video Player */}
                      <div className="video-player-section">
                        <h6 className="thumbnail-label mb-2" style={{ marginTop: 0 }}>Video</h6>
                        <div className="video-thumbnail-container-small">
                            {!isVideoPlaying ? (
                              <div className="video-preview-wrapper-small" onClick={() => setIsVideoPlaying(true)}>
                                <img
                                  src={thumbnailImageUrl || '/placeholder-video.png'}
                                  alt={video.title}
                                  className="video-thumbnail-image-small"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = '/placeholder-video.png';
                                  }}
                                />
                                <div className="video-play-overlay-small">
                                  <div className="video-play-button-small">
                                    <img 
                                      src={getImageUrl('shared/web-admin/common/Play_icon.svg')} 
                                      alt="Play" 
                                      className="play-icon-image"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        const fallbackIcon = document.createElement('i');
                                        fallbackIcon.className = 'ph-play-fill';
                                        target.parentElement?.appendChild(fallbackIcon);
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="video-player-wrapper-small">
                                <video
                                  src={videoUrl || '/placeholder-video.png'}
                                  controls
                                  className="video-player-small"
                                  onError={(e) => {
                                    console.error('Video playback error:', e);
                                    toast.error('Failed to load video');
                                    setIsVideoPlaying(false);
                                  }}
                                >
                                  Your browser does not support the video tag.
                                </video>
                                <button 
                                  className="video-close-button"
                                  onClick={() => setIsVideoPlaying(false)}
                                  title="Close video"
                                >
                                  <i className="ph-x"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                      {/* Thumbnail Image */}
                      <div className="thumbnail-image-section">
                        <h6 className="thumbnail-label mb-2">Thumbnail</h6>
                        <div className="thumbnail-image-container">
                          <img
                            src={thumbnailImageUrl || '/placeholder-video.png'}
                            alt={video.title}
                            className="thumbnail-image"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/placeholder-video.png';
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Show image for image posts (use contentUrl for image posts)
                  <div className="social-image-section">
                    <h6 className="thumbnail-label mb-2" style={{ marginTop: 0 }}>Image</h6>
                    <div className="social-image-container">
                      <img
                          src={video?.contentUrl ? getImageUrl(video.contentUrl) : imageUrl || '/placeholder-video.png'}
                          alt={video?.title || 'Social post'}
                        className="social-detail-image"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder-video.png';
                        }}
                      />
                    </div>
                  </div>
                  )
                ) : (
                  <div className="video-thumbnail-row">
                    {/* Video Player */}
                    <div className="video-player-section">
                      <h6 className="thumbnail-label mb-2" style={{ marginTop: 0 }}>Video</h6>
                      <div className="video-thumbnail-container-small">
                          {!isVideoPlaying ? (
                            <div className="video-preview-wrapper-small" onClick={() => setIsVideoPlaying(true)}>
                              <img
                                src={imageUrl || '/placeholder-video.png'}
                                alt={video.title}
                                className="video-thumbnail-image-small"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/placeholder-video.png';
                                }}
                              />
                              <div className="video-play-overlay-small">
                                <div className="video-play-button-small">
                                  <img 
                                    src={getImageUrl('shared/web-admin/common/Play_icon.svg')} 
                                    alt="Play" 
                                    className="play-icon-image"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                      const fallbackIcon = document.createElement('i');
                                      fallbackIcon.className = 'ph-play-fill';
                                      target.parentElement?.appendChild(fallbackIcon);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="video-player-wrapper-small">
                              <video
                                src={getImageUrl(video.contentUrl)}
                                controls
                                className="video-player-small"
                                onError={(e) => {
                                  console.error('Video playback error:', e);
                                  toast.error('Failed to load video');
                                  setIsVideoPlaying(false);
                                }}
                              >
                                Your browser does not support the video tag.
                              </video>
                              <button 
                                className="video-close-button"
                                onClick={() => setIsVideoPlaying(false)}
                                title="Close video"
                              >
                                <i className="ph-x"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                    {/* Thumbnail Image */}
                    <div className="thumbnail-image-section">
                      <h6 className="thumbnail-label mb-2">Thumbnail</h6>
                      <div className="thumbnail-image-container">
                        <img
                          src={imageUrl || '/placeholder-video.png'}
                          alt="Thumbnail"
                          className="thumbnail-image"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder-video.png';
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* For Level Up, show series details only if videoType is 'series'; for chapters and others, show video details */}
              {category === 'level_up' && video?.videoType === 'series' && series ? (
                <>
                  {/* Series Title */}
                  <div className="detail-field mb-4">
                    <label className="detail-label">Series Title</label>
                    <p className="detail-value">{series.title}</p>
                  </div>

                  {/* Series Description */}
                  <div className="detail-field mb-4">
                    <label className="detail-label">Series Description</label>
                    <p className="detail-value">{series.description || 'N/A'}</p>
                  </div>

                  {/* Series Metadata Grid */}
                  <Row className="metadata-grid">
                    <Col md={6} className="mb-3">
                      <div className="detail-field">
                        <label className="detail-label">Coach</label>
                        <p className="detail-value">{series.coachName || 'N/A'}</p>
                      </div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <div className="detail-field">
                        <label className="detail-label">Difficulty Level</label>
                        <p className="detail-value">{series.difficultyLevel ? series.difficultyLevel.charAt(0).toUpperCase() + series.difficultyLevel.slice(1) : 'N/A'}</p>
                      </div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <div className="detail-field">
                        <label className="detail-label">Sub Category</label>
                        <p className="detail-value">{series.subCategory || 'N/A'}</p>
                      </div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <div className="detail-field">
                        <label className="detail-label">Sport</label>
                        <p className="detail-value">{series.sport || 'N/A'}</p>
                      </div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <div className="detail-field">
                        <label className="detail-label">Created on</label>
                        <p className="detail-value">{formatDate(series.createdAt)}</p>
                      </div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <div className="detail-field">
                        <label className="detail-label">Tags (#)</label>
                        <p className="detail-value">
                          {series.tags && series.tags.length > 0 
                            ? series.tags.map(tag => `#${tag}`).join(' ')
                            : 'N/A'}
                        </p>
                      </div>
                    </Col>
                    {series.thumbnailUrl && (
                      <Col md={6} className="mb-3">
                        <div className="detail-field">
                          <label className="detail-label">Thumbnail</label>
                          <div style={{ marginTop: '8px' }}>
                            <img 
                              src={getImageUrl(series.thumbnailUrl)} 
                              alt="Series thumbnail"
                              style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '4px', objectFit: 'cover' }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/placeholder-video.png';
                              }}
                            />
                          </div>
                        </div>
                      </Col>
                    )}
                    {series.coverImage && (
                      <Col md={6} className="mb-3">
                        <div className="detail-field">
                          <label className="detail-label">Cover Image</label>
                          <div style={{ marginTop: '8px' }}>
                            <img 
                              src={getImageUrl(series.coverImage)} 
                              alt="Series cover"
                              style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '4px', objectFit: 'cover' }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/placeholder-video.png';
                              }}
                            />
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </>
              ) : (
                <>
                  {/* Title */}
                  <div className="detail-field mb-4">
                    <label className="detail-label">Title</label>
                    <p className="detail-value">{video.title}</p>
                  </div>

                  {/* Description */}
                  <div className="detail-field mb-4">
                    <label className="detail-label">Description</label>
                    <p className="detail-value">{video.description || 'N/A'}</p>
                  </div>

                  {/* Metadata Grid */}
                  <Row className="metadata-grid">
                    <Col md={6} className="mb-3">
                      <div className="detail-field">
                        {(() => {
                          // For Action category: Show "Scheduled At" if scheduledPublishAt exists, otherwise "Published At"
                          if (category === 'action') {
                            if (video.scheduledPublishAt) {
                              return (
                                <>
                                  <label className="detail-label">Scheduled At</label>
                                  <p className="detail-value">{formatDate(video.scheduledPublishAt)}</p>
                                </>
                              );
                            } else if (video.publishedAt) {
                              return (
                                <>
                                  <label className="detail-label">Published At</label>
                                  <p className="detail-value">{formatDate(video.publishedAt)}</p>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <label className="detail-label">Created At</label>
                                  <p className="detail-value">{formatDate(video.createdAt)}</p>
                                </>
                              );
                            }
                          } else {
                            // For other categories, keep existing behavior
                            return (
                              <>
                                <label className="detail-label">Published on</label>
                                <p className="detail-value">{formatDate(video.publishedAt || video.createdAt)}</p>
                              </>
                            );
                          }
                        })()}
                      </div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <div className="detail-field">
                        <label className="detail-label">Tags (#)</label>
                        <p className="detail-value">
                          {video.tags && video.tags.length > 0 
                            ? video.tags.map(tag => `#${tag}`).join(' ')
                            : 'N/A'}
                        </p>
                      </div>
                    </Col>
                    <Col md={12} className="mb-3">
                      <div className="detail-field">
                        <label className="detail-label">Video URL</label>
                        <p className="detail-value">
                          <a href={getImageUrl(video.contentUrl)} target="_blank" rel="noopener noreferrer">
                            {getImageUrl(video.contentUrl)}
                          </a>
                        </p>
                      </div>
                    </Col>
                  </Row>
                </>
              )}
                </>
              )}
            </div>
          )}

          {activeTab === 'chapters' && (
            <div className="chapters-tab">
              {isAddingChapters && video ? (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0" style={{ fontSize: '20px', fontWeight: 600, color: '#1a1a1a' }}>Add Chapters</h5>
                    <button 
                      className="btn btn-link text-muted p-0" 
                      onClick={handleCloseAddChaptersForm}
                      style={{ fontSize: '14px', textDecoration: 'none', fontWeight: 500 }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#000'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                    >
                      <i className="ph-x me-1"></i>
                      Close
                    </button>
                  </div>
                  <AddNewCommunityForm
                    show={true}
                    onHide={handleCloseAddChaptersForm}
                    category={category === 'action' ? VideoCategory.ACTION : category === 'level_up' ? VideoCategory.LEVEL_UP : VideoCategory.SOCIAL}
                    onSuccess={handleAddChaptersSuccess}
                    onChapterAdded={handleChapterAdded}
                    video={video}
                    mode="edit"
                    inline={true}
                    initialTab="chapters"
                  />
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-label mb-0">Chapters</h5>
                    {category === 'level_up' && video?.videoType === 'series' && (
                      <button className="btn btn-primary" onClick={handleAddChapters}>
                        <i className="ph-plus me-2"></i>
                        Add New
                      </button>
                    )}
                  </div>

              {chaptersLoading ? (
                <div className="loading-state">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : chapters && chapters.length > 0 ? (
                <Row>
                  {chapters.map((chapter, index) => {
                    // Use chapterNumber from API if available and valid, otherwise use index + 1
                    const displayChapterNumber = (chapter.chapterNumber && chapter.chapterNumber > 0) 
                      ? chapter.chapterNumber 
                      : index + 1;
                    
                    return (
                    <Col key={chapter._id} md={4} className="mb-4">
                      <div 
                        className="chapter-card"
                        onClick={() => handleChapterClick(chapter)}
                        style={{
                          cursor: 'pointer',
                          border: '1px solid #e9ecef',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          transition: 'all 0.2s',
                          backgroundColor: '#fff'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', backgroundColor: '#000' }}>
                          <img
                            src={chapter.thumbnailUrl ? getImageUrl(chapter.thumbnailUrl) : '/placeholder-video.png'}
                            alt={chapter.title}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/placeholder-video.png';
                            }}
                          />
                          <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <i className="ph-play-fill" style={{ color: '#fff', fontSize: '24px', marginLeft: '4px' }}></i>
                          </div>
                          {chapter.duration && (
                            <div style={{
                              position: 'absolute',
                              bottom: '8px',
                              right: '8px',
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: '#fff',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              {formatDuration(chapter.duration)}
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '16px' }}>
                          <h6 style={{ marginBottom: '8px', fontSize: '16px', fontWeight: 600, color: '#212529' }}>
                            {chapter.chapterTitle || `Chapter ${displayChapterNumber}`}
                          </h6>
                          <p style={{ marginBottom: '4px', fontSize: '14px', color: '#6c757d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {chapter.title}
                          </p>
                          {chapter.description && (
                            <p style={{ marginBottom: '8px', fontSize: '12px', color: '#868e96', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {chapter.description}
                            </p>
                          )}
                          {/* Engagement Data */}
                          {chapter.engagement && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '16px', 
                              marginTop: '12px',
                              paddingTop: '12px',
                              borderTop: '1px solid #e9ecef'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <i className="ri-eye-line" style={{ fontSize: '16px', color: '#6c757d' }}></i>
                                <span style={{ fontSize: '13px', color: '#495057', fontWeight: 500 }}>
                                  {formatNumber(chapter.engagement.views || 0)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <i className="ri-heart-line" style={{ fontSize: '16px', color: '#6c757d' }}></i>
                                <span style={{ fontSize: '13px', color: '#495057', fontWeight: 500 }}>
                                  {formatNumber(chapter.engagement.likes || 0)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <i className="ri-chat-1-line" style={{ fontSize: '16px', color: '#6c757d' }}></i>
                                <span style={{ fontSize: '13px', color: '#495057', fontWeight: 500 }}>
                                  {formatNumber(chapter.engagement.comments || 0)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <i className="ri-share-line" style={{ fontSize: '16px', color: '#6c757d' }}></i>
                                <span style={{ fontSize: '13px', color: '#495057', fontWeight: 500 }}>
                                  {formatNumber(chapter.engagement.shares || 0)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                    );
                  })}
                </Row>
              ) : (
                <div className="empty-state">
                  <p className="text-muted">No chapters found for this series.</p>
                </div>
              )}
                </>
              )}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="video-analytics-tab">
              {/* Analytics Header */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                  <h5 className="section-label mb-0" style={{ margin: 0 }}>Analytics</h5>
                  {/* Views Card in Header */}
                  <div 
                    className="analytics-card views-card"
                    style={{ 
                      padding: '10px 18px', 
                      borderRadius: '8px', 
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #90caf9',
                      cursor: 'default',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '8px',
                      minWidth: '100px'
                    }}
                  >
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6c757d', fontWeight: 500, margin: 0 }}>Views:</div>
                    <div className="analytics-card-value" style={{ fontSize: '20px', fontWeight: 700, color: '#212529', lineHeight: '1.2', margin: 0 }}>
                      {analytics?.engagement?.views ? formatNumber(analytics.engagement.views) : '0'}
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <label className="mb-0" style={{ fontSize: '14px', fontWeight: 500, color: '#495057' }}>Filter by</label>
                    <Select
                      value={daysOptions.find(option => option.value === daysFilter)}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setDaysFilter(selectedOption.value);
                          // Reset manual interval flag when days filter changes
                          // This allows auto-interval to work again
                          setIsIntervalManuallySet(false);
                        }
                      }}
                      options={daysOptions}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '38px',
                          fontSize: '14px',
                          width: '150px',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          boxShadow: 'none',
                          '&:hover': {
                            border: '1px solid #dee2e6'
                          }
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999
                        })
                      }}
                      menuPortalTarget={document.body}
                      isSearchable={false}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Select
                      value={intervalOptions.find(option => option.value === intervalFilter)}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setIntervalFilter(selectedOption.value);
                          setIsIntervalManuallySet(true);
                        }
                      }}
                      options={intervalOptions}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '38px',
                          fontSize: '14px',
                          width: '120px',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          boxShadow: 'none',
                          '&:hover': {
                            border: '1px solid #dee2e6'
                          }
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999
                        })
                      }}
                      menuPortalTarget={document.body}
                      isSearchable={false}
                    />
                  </div>
                  {/* Share and Download Stats buttons hidden as per requirements */}
                  {/* <button className="btn btn-success btn-sm">
                    <i className="ph-share-network me-2"></i>
                    Share
                  </button>
                  <button className="btn btn-primary btn-sm">
                    <i className="ph-download me-2"></i>
                    Download Stats
                  </button> */}
                </div>
              </div>

              {analyticsLoading ? (
                <div className="loading-state">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : analytics ? (
                <>
                  {/* Chart Section */}
                  {analytics.chartData && analytics.chartData.data && analytics.chartData.data.length > 0 ? (
                    <div className="analytics-chart-container" style={{ marginTop: '2rem' }}>
                      <div className="chart-wrapper" style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', border: '1px solid #e9ecef', width: '100%', overflowX: 'auto' }}>
                        <SimpleLineChart 
                          data={analytics.chartData.data}
                          metadata={analytics.chartData.metadata}
                          chartType={selectedChartMetric}
                          avgWatchDuration={analytics.viewerBehavior?.avgWatchDuration}
                          videoDuration={analytics.viewerBehavior?.videoDuration}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="analytics-chart-container" style={{ marginTop: '2rem' }}>
                      <div className="chart-placeholder" style={{ backgroundColor: '#fff', padding: '40px', borderRadius: '8px', border: '1px solid #e9ecef', textAlign: 'center' }}>
                        <p className="text-muted mb-0">
                          {analytics.chartData ? 'Chart data is empty' : 'No chart data available'}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <p className="text-muted">No analytics data available</p>
                </div>
              )}
            </div>
          )}
        </div>
      </Container>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        deleteModalFunction={confirmDeleteVideo}
        title={(category === 'level_up' && video?.videoType === 'series') ? 'Delete Series' : 'Delete Video'}
        message={video 
          ? `Are you sure you want to delete "${video.title}"? This action cannot be undone.` 
          : (category === 'level_up' ? 'Are you sure you want to delete this series?' : 'Are you sure you want to delete this video?')}
        confirmLabel={(category === 'level_up' && video?.videoType === 'series') ? 'Delete Series' : 'Delete Video'}
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />

      {/* Publish Confirmation Modal */}
      <Modal show={showPublishModal} onHide={() => setShowPublishModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ph-eye me-2 text-primary"></i>
            Publish Video
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <i className="ph-eye text-primary" style={{ fontSize: '48px' }}></i>
            </div>
            <p className="mb-0">
              Are you sure you want to publish <strong>{video?.title || 'this video'}</strong>? 
              This will make it visible to all users.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowPublishModal(false)}
            disabled={isPublishing}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={confirmPublish}
            disabled={isPublishing}
          >
            {isPublishing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Publishing...
              </>
            ) : (
              <>
                <i className="ph-eye me-2"></i>
                Yes, Publish
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Unpublish Confirmation Modal */}
      <Modal show={showUnpublishModal} onHide={() => setShowUnpublishModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ph-eye-slash me-2 text-warning"></i>
            Unpublish Video
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <i className="ph-eye-slash text-warning" style={{ fontSize: '48px' }}></i>
            </div>
            <p className="mb-0">
              Are you sure you want to unpublish <strong>{video?.title || 'this video'}</strong>? 
              This will hide it from users.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowUnpublishModal(false)}
            disabled={isUnpublishing}
          >
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={confirmUnpublish}
            disabled={isUnpublishing}
          >
            {isUnpublishing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Unpublishing...
              </>
            ) : (
              <>
                <i className="ph-eye-slash me-2"></i>
                Yes, Unpublish
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CommunityVideoDetailPage;

